// PropertyEditControl.tsx - Componente para edição de propriedades

import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Footer } from "@/components/Footer";
import { Address } from "@/components/form-property/address";
import { Leisure } from "@/components/form-property/leisure";
import { PropertyOptions } from "@/components/form-property/PropertyOptions";
import { PropertyTypeForm } from "@/components/form-property/PropertyTypeForm";
import type {
	LeisureData,
	LeisurePayload,
	Owner,
	PhotoItem,
	PropertyFormData,
	PropertyPayload,
} from "@/components/form-property/property-types";
import { OwnerSelector } from "@/components/owner/OwnerSelector";
// Alterado: importar os componentes do lazer do condomínio
import { CondominiumLeisureImageManager } from "@/components/photo/CondominiumLeisureImageManager";
import { CondominiumLeisureImageUpload } from "@/components/photo/CondominiumLeisureImageUpload";
import { ImageManagerWithOrder } from "@/components/photo/ImageManagerWithOrder";
import { ImageUpload } from "@/components/photo/imageUpload";
import { LeisureImageManager } from "@/components/photo/LeisureImageManager";
import { LeisureImageUpload } from "@/components/photo/LeisureImageUpload";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster } from "@/components/ui/toaster";
import { useToast } from "@/components/ui/use-toast";

interface SelectedPhoto {
	id?: number;
	file?: File;
	url: string;
}

interface CaptadorData {
	id: number;
	attributes: {
		username: string;
		email: string;
		collaborator: "BROKER" | "ADMIN";
		blocked: boolean;
	};
}

const defaultLeisure: LeisureData = {
	id: 0,
	partyHall: false,
	adultPool: false,
	gameRoom: false,
	kidsPool: false,
	gym: false,
	playground: false,
	sportsCourt: false,
	sauna: false,
	tennisCourt: false,
	bbqArea: false,
	squashCourt: false,
	socialHall: false,
	wetDeckPool: false,
	lapPool: false,
	kidsBeachPool: false,
	solarium: false,
	poolBar: false,
	massageRoom: false,
	womenSpace: false,
	petPlace: false,
	youthGameRoom: false,
	toyLibrary: false,
	pilatesSpace: false,
	lounge: false,
	gourmetSpace: false,
	kidsPartyRoom: false,
	adultPartyRoomWithBar: false,
	adultGameRoom: false,
	readingRoom: false,
	hammockArea: false,
	zenSpace: false,
	coworkingSpace: false,
	adultTVRoom: false,
	gourmetBar: false,
	cinemaRoom: false,
	sacada: false,
	property: { data: null },
	uploadPhotos: [],
};

export function PropertyEditControl() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { toast } = useToast();
	const route = import.meta.env.VITE_URL_ENDPOINT;

	const [formData, setFormData] = useState<PropertyFormData>({
		id: 0,
		region: "",
		tagKanlo: "",
		street: "",
		neighborhood: "",
		number: "",
		apartmentNumber: "",
		zipCode: "",
		totalArea: "",
		usableArea: "",
		areaConstruida: "",
		areaTerreno: "",
		keyLocation: "",
		keyComplement: "",
		hasCaretakerHouse: false,
		balcony: false,
		condominiumStreet: "",
		condominiumHouseNumber: "",
		commonArea: "",
		privateArea: "",
		status: "",
		condominium: null,
		internalObservation: "",
		externalObservation: "",
		distanceFromBeach: "",
		praia: "",
		price: "",
		condominiumFee: "",
		lado: "",
		barter: false,
		financing: false,
		paymentMethod: "",
		iptu: "",
		video: "",
		seaView: false,
		owner: null,
		block: null,
		city: "",
		apartamentPosition: "",
		captadores: [],
		WCEmployee: false,
		propertyType: "",
		photos: { connect: [] },
		leisure: defaultLeisure,
		condominiumData: undefined,
		apartment: undefined,
		house: undefined,
		commercial_property: undefined,
		area: undefined,
		terrain: undefined,
	});

	const [loading, setLoading] = useState(false);
	const [ownerName, setOwnerName] = useState("");
	const [condominiumName, setCondominiumName] = useState<string | undefined>(
		undefined,
	);
	const [selectedFiles, setSelectedFiles] = useState<SelectedPhoto[]>([]);
	const [photoOrder, setPhotoOrder] = useState<number[]>([]);
	const [leisureImages, setLeisureImages] = useState<
		{ id: number; url: string }[]
	>([]);
	const [condoLeisurePhotos, setCondoLeisurePhotos] = useState<
		{ id: number; url: string }[]
	>([]);
	const [condoLeisureData, setCondoLeisureData] = useState<LeisureData | null>(
		null,
	);
	const [isOwnerModalOpen, setIsOwnerModalOpen] = useState(false);
	const [currentOwner, setCurrentOwner] = useState<Owner | null>(null);

	useEffect(() => {
		const location = formData.keyLocation || "";
		const complement = formData.keyComplement || "";
		let combinedKeys = "";

		if (location && complement) {
			combinedKeys = `${location} - ${complement}`;
		} else if (location) {
			combinedKeys = location;
		}

		if (combinedKeys !== formData.keys) {
			setFormData((prev) => ({
				...prev,
				keys: combinedKeys,
			}));
		}
	}, [formData.keyLocation, formData.keyComplement]);

	const fetchCondoLeisure = useCallback(async () => {
		try {
			const condoId = formData.condominium;
			if (!condoId) return;

			const res = await fetch(
				`${route}/api/condominiums/${condoId}?populate=leisure`,
			);
			if (res.ok) {
				const cData = await res.json();
				const condoLeisure = cData.data.attributes.leisure?.data;
				if (condoLeisure) {
					setCondoLeisureData({
						id: condoLeisure.id,
						...condoLeisure.attributes,
					});

					const leisureRes = await fetch(
						`${route}/api/leisures/${condoLeisure.id}?populate=photos`,
					);
					if (leisureRes.ok) {
						const lData = await leisureRes.json();
						const photos = lData.data.attributes.photos;
						if (photos && "data" in photos && Array.isArray(photos.data)) {
							const photoObjs = photos.data.map((p: PhotoItem) => ({
								id: p.id,
								url: p.attributes.url.startsWith("http")
									? p.attributes.url
									: `${route}${p.attributes.url}`,
							}));
							setCondoLeisurePhotos(photoObjs);
						}
					}
				}
			}
		} catch (err) {
			console.error("Erro ao buscar lazer do condomínio", err);
		}
	}, [formData.condominium, route]);

	const fetchProperty = useCallback(async () => {
		try {
			const response = await fetch(
				`${route}/api/properties/${id}?populate=leisure,owner,condominium,photos,leisure.photos,captadores`,
			);
			const result = await response.json();
			if (!response.ok) {
				throw new Error("Erro ao buscar a propriedade");
			}
			const prop = result.data.attributes;
			const leisureData = prop.leisure?.data;

			const fetchedOwnerName = prop.owner?.data?.attributes?.name || "";
			const fetchedCondominiumName = prop.condominium?.data?.attributes?.Name;
			const lastEditor = localStorage.getItem("userName");

			// Definir o proprietário atual para o OwnerSelector
			if (prop.owner?.data) {
				setCurrentOwner({
					id: prop.owner.data.id,
					attributes: prop.owner.data.attributes,
				});
			}

			if (prop.photos && Array.isArray(prop.photos.data)) {
				// Criar mapa de ID -> foto para facilitar reordenação
				const photoMap = new Map<number, SelectedPhoto>();
				prop.photos.data.forEach((p: PhotoItem) => {
					photoMap.set(p.id, {
						id: p.id,
						url: p.attributes.url.startsWith("http")
							? p.attributes.url
							: `${route}${p.attributes.url}`,
					});
				});

				// Aplicar photoOrder se existir
				let orderedPhotos: SelectedPhoto[] = [];
				if (
					prop.photoOrder &&
					Array.isArray(prop.photoOrder) &&
					prop.photoOrder.length > 0
				) {
					// Verificar se IDs são válidos
					const hasValidIds = prop.photoOrder.every((id: number) =>
						photoMap.has(id),
					);

					if (hasValidIds) {
						// Reordenar usando photoOrder
						prop.photoOrder.forEach((photoId: number) => {
							const photo = photoMap.get(photoId);
							if (photo) {
								orderedPhotos.push(photo);
							}
						});

						// Adicionar fotos que não estão no photoOrder no final
						photoMap.forEach((photo, id) => {
							if (!prop.photoOrder!.includes(id)) {
								orderedPhotos.push(photo);
							}
						});

						setPhotoOrder(prop.photoOrder);
					} else {
						// photoOrder inválido, usar ordem padrão
						orderedPhotos = Array.from(photoMap.values());
						setPhotoOrder(Array.from(photoMap.keys()));
					}
				} else {
					// Sem photoOrder, usar ordem padrão
					orderedPhotos = Array.from(photoMap.values());
					setPhotoOrder(Array.from(photoMap.keys()));
				}

				setSelectedFiles(orderedPhotos);
			}

			let leisurePhotoObjects: { id: number; url: string }[] = [];
			if (
				leisureData &&
				leisureData.attributes.photos &&
				Array.isArray(leisureData.attributes.photos.data)
			) {
				leisurePhotoObjects = leisureData.attributes.photos.data.map(
					(p: PhotoItem) => ({
						id: p.id,
						url: p.attributes.url.startsWith("http")
							? p.attributes.url
							: `${route}${p.attributes.url}`,
					}),
				);
				setLeisureImages(leisurePhotoObjects);
			}
			const keysValue = prop.keys || "";
			let keyLocation = "";
			let keyComplement = "";

			if (keysValue.includes(" - ")) {
				[keyLocation, keyComplement] = keysValue.split(" - ");
			} else {
				keyLocation = keysValue;
			}

			const updatedFormData: PropertyFormData = {
				id: Number(id),
				region: prop.region || "",
				street: prop.street || "",
				neighborhood: prop.neighborhood || "",
				number: prop.number || "",
				keyLocation,
				keyComplement,
				hasCaretakerHouse: prop.hasCaretakerHouse || false,
				balcony: prop.balcony || false,
				condominiumStreet: prop.condominiumStreet || "",
				condominiumHouseNumber: prop.condominiumHouseNumber || "",
				tagKanlo: prop.tagKanlo || "",
				apartmentNumber: prop.apartmentNumber || "",
				zipCode: prop.zipCode || "",
				city: prop.city || "",
				totalArea: prop.totalArea || "",
				usableArea: prop.usableArea || "",
				areaConstruida: prop.areaConstruida || "",
				areaTerreno: prop.areaTerreno || "",
				commonArea: prop.commonArea || "",
				privateArea: prop.privateArea || "",
				status: prop.status || "",
				internalObservation: prop.internalObservation || "",
				externalObservation: prop.externalObservation || "",
				distanceFromBeach: prop.distanceFromBeach || "",
				praia: prop.praia || "",
				price: prop.price || "",
				condominiumFee: prop.condominiumFee || "",
				barter: prop.barter || false,
				financing: prop.financing || false,
				paymentMethod: prop.paymentMethod || "",
				iptu: prop.iptu || "",
				video: prop.video || "",
				seaView: prop.seaView || false,
				owner: prop.owner?.data?.id || null,
				block: prop.block ?? null,
				condominium: prop.condominium?.data?.id || null,
				propertyType: prop.propertyType || "",
				captadores: Array.isArray(prop.captadores?.data)
					? prop.captadores.data.map((captador: CaptadorData) => captador.id)
					: [],
				apartamentPosition: prop.apartamentPosition ?? "",
				WCEmployee: prop.WCEmployee ?? false,
				lado: prop.lado || "",
				leisure: {
					...defaultLeisure,
					...(leisureData?.attributes || {}),
					id: leisureData?.id || 0,
					property: { data: null },
				},
				lastEditor: lastEditor || "",
				condominiumData: prop.condominium?.data || undefined,
				apartment:
					prop.propertyType === "apartment"
						? {
								type: prop.type || "",
								numberOfBedrooms: prop.numberOfBedrooms || "",
								numberOfSuites: prop.numberOfSuites || "",
								numberOfRooms: prop.numberOfRooms || "",
								socialBathroom: prop.socialBathroom || "",
								halfBath: prop.halfBath || "",
								kitchen: prop.kitchen || "",
								apartmentNumber: prop.apartmentNumber || "",
								garage: prop.garage || "",
								intercom: prop.intercom || false,
								maidsQuarters: prop.maidsQuarters || false,
								laundryArea: prop.laundryArea || false,
								elevator: prop.elevator || false,
								furnished: prop.furnished || false,
								furnitureNotes: prop.furnitureNotes || "",
								apartamentPosition: prop.apartamentPosition || "",
								WCEmployee: prop.WCEmployee || false,
							}
						: undefined,
				house:
					prop.propertyType === "house"
						? {
								type: prop.type || "",
								numberOfBedrooms: prop.numberOfBedrooms || "",
								numberOfSuites: prop.numberOfSuites || "",
								livingRoom: prop.livingRoom || "",
								socialBathroom: prop.socialBathroom || "",
								halfBath: prop.halfBath || "",
								kitchen: prop.kitchen || "",
								laundryRoom: prop.laundryRoom || "",
								hasCaretakerHouse: prop.hasCaretakerHouse || false,
								condominiumStreet: prop.condominiumStreet || "",
								condominiumHouseNumber: prop.condominiumHouseNumber || "",
								pantry: prop.pantry || "",
								closet: prop.closet || "",
								garage: prop.garage || "",
								intercom: prop.intercom || false,
								WCEmployee: prop.WCEmployee || false,
							}
						: undefined,
				commercial_property:
					prop.propertyType === "commercial_property"
						? {
								type: prop.type || "",
								cabinets: prop.cabinets || "",
								bathrooms: prop.bathrooms || "",
								conservation: prop.conservation || "",
								guardhouse: prop.guardhouse || "",
								numberOfRooms: prop.numberOfRooms || "",
								kitchen: prop.kitchen || "",
								copa: prop.copa || "",
								keys: prop.keys || "",
								balcony: prop.balcony || false,
								intercom: prop.intercom || false,
							}
						: undefined,
				area:
					prop.propertyType === "area"
						? {
								classification: prop.classification || "",
								type: prop.type || "",
								front: prop.front || "",
								back: prop.back || "",
								rightSide: prop.rightSide || "",
								leftSide: prop.leftSide || "",
								meioDeQuadra: prop.meioDeQuadra || "",
								lote: prop.lote || "",
								loteamento: prop.loteamento || "",
								quadra: prop.quadra || "",
								corner: prop.corner || false,
								leveled: prop.leveled || false,
								fenced: prop.fenced || false,
								waterSupply: prop.waterSupply || false,
								electricitySupply: prop.electricitySupply || false,
							}
						: undefined,
				terrain:
					prop.propertyType === "terrain"
						? {
								type: prop.type || "",
								front: prop.front || "",
								back: prop.back || "",
								rightSide: prop.rightSide || "",
								leftSide: prop.leftSide || "",
								meioDeQuadra: prop.meioDeQuadra || "",
								lote: prop.lote || "",
								loteamento: prop.loteamento || "",
								quadra: prop.quadra || "",
								corner: prop.corner || false,
								leveled: prop.leveled || false,
								fenced: prop.fenced || false,
								waterSupply: prop.waterSupply || false,
								electricitySupply: prop.electricitySupply || false,
							}
						: undefined,
			};

			setFormData(updatedFormData);
			setOwnerName(fetchedOwnerName);
			setCondominiumName(fetchedCondominiumName);
		} catch (error) {
			console.error("Erro ao buscar o Imóvel:", error);
			toast({
				title: "Erro",
				description: "Não foi possível carregar os dados do Imóvel.",
				variant: "destructive",
			});
		}
	}, [id, route, toast]);

	useEffect(() => {
		if (id) {
			fetchProperty();
			fetchCondoLeisure();
		}
	}, [id, fetchProperty, fetchCondoLeisure]);

	const handleChange = (
		e: React.ChangeEvent<
			HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
		>,
	) => {
		const { name, value, type } = e.target;
		let newValue: string | number | boolean | null = value;
		if (type === "checkbox") {
			newValue = (e.target as HTMLInputElement).checked;
		}
		if (["owner", "block", "condominium"].includes(name)) return;
		setFormData((prevState: PropertyFormData) => ({
			...prevState,
			[name]: newValue,
		}));
	};

	const handleLeisureChange = (field: keyof LeisureData, value: unknown) => {
		setFormData((prev) => ({
			...prev,
			leisure: {
				...prev.leisure,
				[field]: value,
			},
		}));
	};

	const handleLeisureCheckboxChange = (
		name: keyof PropertyFormData["leisure"],
		checked: boolean | "indeterminate",
	) => {
		setFormData((prevState: PropertyFormData) => ({
			...prevState,
			leisure: {
				...prevState.leisure,
				[name]: checked === true,
			},
		}));
	};

	const handleSubmit = async () => {
		setLoading(true);
		try {
			// Pegar ordem atual das fotos do componente
			const getCurrentPhotoOrder = (window as any).__getCurrentPhotoOrder;
			const currentPhotoOrder = getCurrentPhotoOrder
				? getCurrentPhotoOrder()
				: photoOrder;

			const lastEditor = localStorage.getItem("userName");

			// 1. Upload de novas fotos do imóvel (se houver)
			const newPhotos = selectedFiles.filter((photo) => photo.file) as {
				file: File;
				url: string;
			}[];
			const uploadedPhotoIds: number[] = [];
			if (newPhotos.length > 0) {
				for (const photo of newPhotos) {
					const fd = new FormData();
					fd.append("files", photo.file);
					try {
						const uploadResponse = await fetch(`${route}/api/upload`, {
							method: "POST",
							body: fd,
						});
						if (!uploadResponse.ok) {
							throw new Error(
								`Erro ao fazer upload da imagem: ${uploadResponse.statusText}`,
							);
						}
						const uploadResult = await uploadResponse.json();
						if (Array.isArray(uploadResult)) {
							uploadedPhotoIds.push(uploadResult[0].id);
						} else {
							uploadedPhotoIds.push(uploadResult.id);
						}
					} catch (uploadError) {
						console.error("Erro durante upload:", uploadError);
						throw new Error("Falha ao fazer upload das novas imagens.");
					}
				}
			}

			// 2. Atualizar (ou criar) o registro de lazer
			let leisureId = formData.leisure.id;
			const leisurePayload: LeisurePayload = {
				data: {
					id: formData.leisure.id, // obrigatório
					linkPhotos: formData.leisure.linkPhotos || "",
					partyHall: formData.leisure.partyHall,
					adultPool: formData.leisure.adultPool,
					gameRoom: formData.leisure.gameRoom,
					kidsPool: formData.leisure.kidsPool,
					gym: formData.leisure.gym,
					playground: formData.leisure.playground,
					sportsCourt: formData.leisure.sportsCourt,
					sauna: formData.leisure.sauna,
					tennisCourt: formData.leisure.tennisCourt,
					bbqArea: formData.leisure.bbqArea,
					squashCourt: formData.leisure.squashCourt,
					socialHall: formData.leisure.socialHall,
					wetDeckPool: formData.leisure.wetDeckPool,
					lapPool: formData.leisure.lapPool,
					kidsBeachPool: formData.leisure.kidsBeachPool,
					solarium: formData.leisure.solarium,
					poolBar: formData.leisure.poolBar,
					massageRoom: formData.leisure.massageRoom,
					womenSpace: formData.leisure.womenSpace,
					petPlace: formData.leisure.petPlace,
					youthGameRoom: formData.leisure.youthGameRoom,
					toyLibrary: formData.leisure.toyLibrary,
					pilatesSpace: formData.leisure.pilatesSpace,
					lounge: formData.leisure.lounge,
					gourmetSpace: formData.leisure.gourmetSpace,
					kidsPartyRoom: formData.leisure.kidsPartyRoom,
					adultPartyRoomWithBar: formData.leisure.adultPartyRoomWithBar,
					adultGameRoom: formData.leisure.adultGameRoom,
					readingRoom: formData.leisure.readingRoom,
					hammockArea: formData.leisure.hammockArea,
					zenSpace: formData.leisure.zenSpace,
					coworkingSpace: formData.leisure.coworkingSpace,
					adultTVRoom: formData.leisure.adultTVRoom,
					gourmetBar: formData.leisure.gourmetBar,
					cinemaRoom: formData.leisure.cinemaRoom,
					saunaSeca: formData.leisure.saunaSeca,
					saunaUmida: formData.leisure.saunaUmida,
					saunaComDescanso: formData.leisure.saunaComDescanso,
					varanda: formData.leisure.varanda,
					terraco: formData.leisure.terraco,
					sacada: formData.leisure.sacada,
					outros: formData.leisure.outros || "",
					property: formData.leisure.property, // obrigatório
				},
			};

			if (leisureId && leisureId > 0) {
				const leisureUpdateResponse = await fetch(
					`${route}/api/leisures/${leisureId}`,
					{
						method: "PUT",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify(leisurePayload),
					},
				);
				if (!leisureUpdateResponse.ok) {
					throw new Error("Erro ao atualizar os dados de lazer.");
				}
				const leisureUpdateResult = await leisureUpdateResponse.json();
				leisureId = leisureUpdateResult.data.id;
			} else {
				const leisureCreateResponse = await fetch(`${route}/api/leisures`, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify(leisurePayload),
				});
				if (!leisureCreateResponse.ok) {
					throw new Error("Erro ao criar os dados de lazer.");
				}
				const leisureCreateResult = await leisureCreateResponse.json();
				leisureId = leisureCreateResult.data.id;
			}

			// 3. Montar o payload completo da propriedade com todos os campos
			const allPhotoIds = [
				...selectedFiles.filter((p) => p.id).map((p) => p.id!),
				...uploadedPhotoIds,
			];

			const propertyPayload: PropertyPayload = {
				data: {
					region: formData.region,
					street: formData.street,
					neighborhood: formData.neighborhood,
					number: formData.number,
					tagKanlo: formData.tagKanlo,
					apartmentNumber: formData.apartmentNumber,
					zipCode: formData.zipCode,
					totalArea: formData.totalArea,
					usableArea: formData.usableArea,
					areaConstruida: formData.areaConstruida,
					areaTerreno: formData.areaTerreno,
					commonArea: formData.commonArea,
					privateArea: formData.privateArea,
					status: formData.status,
					internalObservation: formData.internalObservation,
					externalObservation: formData.externalObservation,
					distanceFromBeach: formData.distanceFromBeach,
					praia: formData.praia,
					price: formData.price,
					condominiumFee: formData.condominiumFee,
					barter: formData.barter,
					financing: formData.financing,
					paymentMethod: formData.paymentMethod,
					iptu: formData.iptu,
					keys: formData.keys,
					hasCaretakerHouse: formData.hasCaretakerHouse,
					balcony: formData.balcony,
					condominiumStreet: formData.condominiumStreet,
					condominiumHouseNumber: formData.condominiumHouseNumber,
					seaView: formData.seaView,
					lado: formData.lado,
					owner: formData.owner || null,
					block: formData.block || null,
					condominium: formData.condominium || null,
					propertyType: formData.propertyType,
					photoOrder:
						currentPhotoOrder.length > 0 ? currentPhotoOrder : allPhotoIds,
					leisure: leisureId,
					lastEditor: lastEditor || "",
					video: formData.video,
					workFund: formData.workFund,
					captadores: formData.captadores,
					apartamentPosition: formData.apartamentPosition,
					WCEmployee: formData.WCEmployee,
					city: formData.city,
					...(formData.propertyType === "apartment" && formData.apartment
						? { ...formData.apartment }
						: {}),
					...(formData.propertyType === "house" && formData.house
						? { ...formData.house }
						: {}),
					...(formData.propertyType === "commercial_property" &&
					formData.commercial_property
						? { ...formData.commercial_property }
						: {}),
					...(formData.propertyType === "area" && formData.area
						? { ...formData.area }
						: {}),
					...(formData.propertyType === "terrain" && formData.terrain
						? { ...formData.terrain }
						: {}),
				},
			};

			const propertyResponse = await fetch(`${route}/api/properties/${id}`, {
				method: "PUT",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(propertyPayload),
			});
			const propertyResult = await propertyResponse.json();
			if (!propertyResponse.ok) {
				console.error("Erro ao editar a propriedade:", propertyResult);
				throw new Error("Erro ao editar a propriedade.");
			}

			toast({
				title: "Sucesso",
				description: "Imóvel e dados de lazer atualizados com sucesso!",
			});
			setTimeout(() => {
				navigate(`/propriedade/${id}`);
			}, 2000);
		} catch (error) {
			console.error("Erro ao enviar os dados de edição:", error);
			toast({
				title: "Erro",
				description: "Erro ao editar o imóvel. Por favor, tente novamente.",
				variant: "destructive",
			});
		} finally {
			setLoading(false);
		}
	};

	const handleOwnerChange = async () => {
		try {
			// Atualizar o proprietário na propriedade
			const propertyPayload = {
				data: {
					owner: formData.owner,
				},
			};

			const response = await fetch(`${route}/api/properties/${id}`, {
				method: "PUT",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(propertyPayload),
			});

			if (!response.ok) {
				throw new Error("Erro ao atualizar proprietário");
			}

			toast({
				title: "Sucesso",
				description: "Proprietário atualizado com sucesso!",
			});

			setIsOwnerModalOpen(false);
			// Recarregar os dados da propriedade para atualizar o nome do proprietário
			fetchProperty();
		} catch (error) {
			console.error("Erro ao atualizar proprietário:", error);
			toast({
				title: "Erro",
				description: "Erro ao atualizar proprietário. Tente novamente.",
				variant: "destructive",
			});
		}
	};

	return (
		<div className="flex h-full w-full flex-col bg-gray-100 p-8">
			<h1 className="mb-6 text-center text-3xl font-bold text-gray-800">
				Edição de Imóvel
			</h1>
			<div className="rounded-lg bg-white p-6 shadow-md">
				{ownerName ? (
					<div className="mb-4">
						<label className="block font-semibold text-gray-700">
							Proprietário:
						</label>
						<div className="flex items-center gap-2">
							<input
								type="text"
								value={ownerName}
								disabled
								className="mt-1 flex-1 rounded border-gray-300 bg-gray-100 p-2"
							/>
							<Dialog
								open={isOwnerModalOpen}
								onOpenChange={setIsOwnerModalOpen}
							>
								<DialogTrigger asChild>
									<Button
										type="button"
										variant="outline"
										className="mt-1 text-blue-600 hover:text-blue-800"
									>
										Alterar Proprietário
									</Button>
								</DialogTrigger>
								<DialogContent className="max-w-md">
									<DialogHeader>
										<DialogTitle>Alterar Proprietário</DialogTitle>
									</DialogHeader>
									<OwnerSelector
										setFormData={setFormData}
										selectedOwner={currentOwner}
									/>
									<div className="mt-4 flex justify-end gap-2">
										<Button
											type="button"
											variant="outline"
											onClick={() => setIsOwnerModalOpen(false)}
										>
											Cancelar
										</Button>
										<Button type="button" onClick={handleOwnerChange}>
											Confirmar
										</Button>
									</div>
								</DialogContent>
							</Dialog>
						</div>
					</div>
				) : (
					<div className="mb-4">
						<label className="block font-semibold text-gray-700">
							Proprietário:
						</label>
						<div className="flex items-center gap-2">
							<p className="mt-1 flex-1 text-gray-500">
								Não possui proprietário
							</p>
							<Dialog
								open={isOwnerModalOpen}
								onOpenChange={setIsOwnerModalOpen}
							>
								<DialogTrigger asChild>
									<Button
										type="button"
										variant="outline"
										className="mt-1 text-blue-600 hover:text-blue-800"
									>
										Adicionar Proprietário
									</Button>
								</DialogTrigger>
								<DialogContent className="max-w-md">
									<DialogHeader>
										<DialogTitle>Adicionar Proprietário</DialogTitle>
									</DialogHeader>
									<OwnerSelector
										setFormData={setFormData}
										selectedOwner={null}
									/>
									<div className="mt-4 flex justify-end gap-2">
										<Button
											type="button"
											variant="outline"
											onClick={() => setIsOwnerModalOpen(false)}
										>
											Cancelar
										</Button>
										<Button type="button" onClick={handleOwnerChange}>
											Confirmar
										</Button>
									</div>
								</DialogContent>
							</Dialog>
						</div>
					</div>
				)}
				{condominiumName && (
					<div className="mb-4">
						<label className="block font-semibold text-gray-700">
							Condomínio:
						</label>
						<input
							type="text"
							value={condominiumName}
							disabled
							className="mt-1 w-full rounded border-gray-300 bg-gray-100 p-2"
						/>
					</div>
				)}

				<Address
					data={formData}
					onChange={handleChange}
					setFormData={setFormData}
				/>
				<PropertyOptions formData={formData} setFormData={setFormData} />
				<PropertyTypeForm formData={formData} setFormData={setFormData} />
				<Leisure
					data={formData.leisure}
					onChange={handleLeisureChange}
					onCheckboxChange={handleLeisureCheckboxChange}
					isUpdate={true}
				/>

				{/* Imagens do Imóvel */}
				<div className="mb-4 mt-10">
					<Label className="bold text-2xl text-gray-800">
						Imagens do Imóvel
					</Label>
				</div>
				<div className="mb-8">
					<ImageManagerWithOrder
						images={selectedFiles.map((p) => ({
							id: p.id!,
							url: p.url,
						}))}
						propertyId={Number(id)}
						onImageDeleted={fetchProperty}
					/>
					<ImageUpload
						propertyId={Number(id)}
						existingImagesCount={selectedFiles.length}
						onUploadComplete={fetchProperty}
					/>
				</div>

				{/* Imagens do Lazer do Imóvel */}
				<div className="mb-4 mt-10">
					<Label className="bold text-2xl text-gray-800">
						Imagens do Lazer do Imóvel
					</Label>
				</div>
				<div className="mb-8">
					<LeisureImageManager
						images={leisureImages}
						leisureId={formData.leisure.id}
						onImageDeleted={fetchProperty}
					/>
					<LeisureImageUpload
						leisureId={formData.leisure.id}
						existingImagesCount={leisureImages.length}
						onUploadComplete={fetchProperty}
					/>
				</div>

				{condoLeisureData && (
					<>
						<div className="mb-4 mt-10">
							<Label className="bold text-2xl text-gray-800">
								Imagens do Lazer do Condomínio
							</Label>
						</div>
						<div className="mb-8">
							<CondominiumLeisureImageManager
								images={condoLeisurePhotos}
								leisureId={condoLeisureData.id}
								onImageDeleted={fetchCondoLeisure}
							/>
							<CondominiumLeisureImageUpload
								leisureId={condoLeisureData.id}
								existingImagesCount={condoLeisurePhotos.length}
								onUploadComplete={fetchCondoLeisure}
							/>
						</div>
					</>
				)}

				<div className="flex flex-col">
					<Label htmlFor="video" className="mt-5 text-gray-600">
						Id do Vídeo do Youtube
					</Label>
					<Input
						id="video"
						name="video"
						placeholder="Ex: gfc3YircI-g"
						value={formData.video || ""}
						onChange={(e) =>
							setFormData((prevState) => ({
								...prevState,
								video: e.target.value,
							}))
						}
						className="mt-1"
					/>
				</div>
				<div className="mt-6 flex justify-end">
					<Button
						onClick={handleSubmit}
						disabled={loading}
						className="bg-indigo-600 text-white hover:bg-indigo-700"
					>
						{loading ? "Enviando..." : "Salvar Alterações"}
					</Button>
				</div>
			</div>
			<Toaster />
			<Footer />
		</div>
	);
}
